import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { resolveUrl } from '../../utils/api';

function MediaPreview({ url, type, name }) {
  if (!url) return null;
  const abs = resolveUrl(url);
  if (type?.startsWith('image/')) return <img src={abs} alt={name} style={S.thumb} onError={e => e.target.style.display = 'none'} />;
  if (type?.startsWith('video/')) return <video src={abs} style={S.thumb} muted controls />;
  return <a href={abs} target="_blank" rel="noreferrer" style={{ color: '#818cf8', fontSize: 10 }}>📎 {name}</a>;
}

export default function AdminRandomChat() {
  const [rooms, setRooms] = useState([]);
  const [totalRooms, setTotalRooms] = useState(0);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomMessages, setRoomMessages] = useState([]);
  const [msgsLoading, setMsgsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editMsg, setEditMsg] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [confirmDeleteMsg, setConfirmDeleteMsg] = useState(null);
  const [confirmDeleteRoom, setConfirmDeleteRoom] = useState(null);

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ search });
      const res = await api.get(`/admin/random-chat/rooms/?${params}`);
      setRooms(res.data.rooms);
      setTotalRooms(res.data.total);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  const loadRoomMessages = async (room) => {
    setSelectedRoom(room);
    setMsgsLoading(true);
    try {
      const res = await api.get(`/admin/random-chat/messages/?room_id=${room.id}`);
      setRoomMessages(res.data.messages);
    } catch (e) {
      console.error(e);
    } finally { setMsgsLoading(false); }
  };

  const deleteMessage = async (msg) => {
    try {
      await api.delete(`/admin/random-chat/messages/${msg.id}/`);
      setRoomMessages(prev => prev.filter(m => m.id !== msg.id));
      setConfirmDeleteMsg(null);
    } catch (e) { alert('Delete failed'); }
  };

  const saveMessageEdit = async () => {
    try {
      await api.put(`/admin/random-chat/messages/${editMsg.id}/`, { content: editContent });
      setRoomMessages(prev => prev.map(m => m.id === editMsg.id ? { ...m, content: editContent } : m));
      setEditMsg(null);
    } catch (e) { alert('Save failed'); }
  };

  const deleteRoom = async (room) => {
    try {
      await api.delete(`/admin/random-chat/rooms/${room.id}/`);
      setRooms(prev => prev.filter(r => r.id !== room.id));
      if (selectedRoom?.id === room.id) { setSelectedRoom(null); setRoomMessages([]); }
      setConfirmDeleteRoom(null);
    } catch (e) { alert('Delete failed'); }
  };

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>🎲 Random Chat</h1>
          <p style={S.subtitle}>{totalRooms} rooms total</p>
        </div>
      </div>

      <input
        style={S.searchInput}
        placeholder="Search by username..."
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      <div style={S.split}>
        {/* Room list */}
        <div style={S.roomList}>
          <div style={S.listHdr}>Rooms</div>
          {loading ? (
            <div style={S.center}><div style={S.spinner} /></div>
          ) : rooms.length === 0 ? (
            <div style={S.empty}>No rooms found</div>
          ) : rooms.map(room => (
            <div
              key={room.id}
              style={{ ...S.roomItem, ...(selectedRoom?.id === room.id ? S.roomItemActive : {}) }}
              onClick={() => loadRoomMessages(room)}
            >
              <div style={S.roomUsers}>
                <span style={{ color: '#818cf8', fontWeight: 600, fontSize: 11 }}>{room.user1_username}</span>
                <span style={{ color: '#475569', fontSize: 10 }}> ↔ </span>
                <span style={{ color: '#94a3b8', fontSize: 11 }}>{room.user2_username}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                <span style={{ fontSize: 10, color: '#475569' }}>{room.message_count} msgs · {new Date(room.created_at).toLocaleDateString()}</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {room.is_active && <span style={S.activeBadge}>Active</span>}
                  <button
                    style={S.dangerMiniBtn}
                    onClick={(e) => { e.stopPropagation(); setConfirmDeleteRoom(room); }}
                    title="Delete room"
                  >🗑️</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Messages panel */}
        <div style={S.msgPanel}>
          {!selectedRoom ? (
            <div style={S.center}>
              <div style={{ textAlign: 'center', color: '#475569' }}>
                <div style={{ fontSize: 30, marginBottom: 12 }}>🎲</div>
                <p>Select a room to view messages</p>
              </div>
            </div>
          ) : (
            <>
              <div style={S.msgPanelHdr}>
                <span style={{ fontWeight: 700, color: '#e2e8f0', fontSize: 12 }}>
                  {selectedRoom.user1_username} ↔ {selectedRoom.user2_username}
                </span>
                <span style={{ color: '#475569', fontSize: 10 }}>Room: {selectedRoom.room_id}</span>
              </div>
              <div style={S.msgScroll}>
                {msgsLoading ? (
                  <div style={S.center}><div style={S.spinner} /></div>
                ) : roomMessages.length === 0 ? (
                  <div style={S.empty}>No messages in this room</div>
                ) : roomMessages.map(m => {
                  const isUser1 = m.sender_username === selectedRoom.user1_username;
                  return (
                    <div key={m.id} style={{ ...S.msgRow, alignSelf: isUser1 ? 'flex-end' : 'flex-start' }}>
                      <div style={S.msgSender}>{m.sender_username}</div>
                      {m.is_unsent ? (
                        <em style={{ color: '#475569', fontSize: 10 }}>Message unsent</em>
                      ) : (
                        <div style={{ color: '#e2e8f0', fontSize: 11 }}>{m.content}</div>
                      )}
                      <MediaPreview url={m.file_url} type={m.file_type} name={m.original_filename} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                        <span style={{ fontSize: 10, color: '#475569' }}>{new Date(m.timestamp).toLocaleTimeString()}</span>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button style={S.miniEdit} onClick={() => { setEditMsg(m); setEditContent(m.content || ''); }}>✏️</button>
                          <button style={S.miniDel} onClick={() => setConfirmDeleteMsg(m)}>🗑️</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Edit message modal */}
      {editMsg && (
        <div style={S.modalOverlay} onClick={() => setEditMsg(null)}>
          <div style={{ ...S.modal, maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div style={S.modalHdr}>
              <span style={{ fontWeight: 700, color: '#e2e8f0' }}>✏️ Edit Message</span>
              <button style={S.closeBtn} onClick={() => setEditMsg(null)}>✕</button>
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <textarea style={S.input} value={editContent} onChange={e => setEditContent(e.target.value)} rows={4} />
              <button style={S.saveBtn} onClick={saveMessageEdit}>💾 Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete message */}
      {confirmDeleteMsg && (
        <div style={S.modalOverlay} onClick={() => setConfirmDeleteMsg(null)}>
          <div style={{ ...S.modal, maxWidth: 360 }} onClick={e => e.stopPropagation()}>
            <div style={S.modalHdr}>
              <span style={{ fontWeight: 700, color: '#f43f5e' }}>⚠️ Delete Message?</span>
              <button style={S.closeBtn} onClick={() => setConfirmDeleteMsg(null)}>✕</button>
            </div>
            <div style={{ padding: 20 }}>
              <p style={{ color: '#94a3b8', margin: '0 0 16px', fontSize: 12 }}>This will permanently delete the message.</p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button style={S.cancelBtn} onClick={() => setConfirmDeleteMsg(null)}>Cancel</button>
                <button style={S.dangerBtn} onClick={() => deleteMessage(confirmDeleteMsg)}>🗑️ Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete room */}
      {confirmDeleteRoom && (
        <div style={S.modalOverlay} onClick={() => setConfirmDeleteRoom(null)}>
          <div style={{ ...S.modal, maxWidth: 360 }} onClick={e => e.stopPropagation()}>
            <div style={S.modalHdr}>
              <span style={{ fontWeight: 700, color: '#f43f5e' }}>⚠️ Delete Room?</span>
              <button style={S.closeBtn} onClick={() => setConfirmDeleteRoom(null)}>✕</button>
            </div>
            <div style={{ padding: 20 }}>
              <p style={{ color: '#94a3b8', margin: '0 0 16px', fontSize: 12 }}>
                Delete room and all {confirmDeleteRoom.message_count} messages? This cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button style={S.cancelBtn} onClick={() => setConfirmDeleteRoom(null)}>Cancel</button>
                <button style={S.dangerBtn} onClick={() => deleteRoom(confirmDeleteRoom)}>🗑️ Delete Room</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  root: { flex: 1, overflow: 'hidden', padding: 24, background: '#07070d', display: 'flex', flexDirection: 'column', gap: 14, animation: 'fadeIn 0.3s ease' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
  title: { margin: 0, fontSize: 20, fontWeight: 800, color: '#e2e8f0' },
  subtitle: { margin: '4px 0 0', color: '#475569', fontSize: 11 },
  searchInput: { padding: '10px 16px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#e2e8f0', fontSize: 12, outline: 'none', width: '100%', maxWidth: 400, boxSizing: 'border-box' },
  split: { display: 'flex', gap: 16, flex: 1, overflow: 'hidden' },
  roomList: { width: 280, flexShrink: 0, overflowY: 'auto', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, display: 'flex', flexDirection: 'column' },
  listHdr: { padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', fontWeight: 700, color: '#64748b', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' },
  roomItem: { padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer', transition: 'background 0.15s' },
  roomItemActive: { background: 'rgba(99,102,241,0.1)' },
  roomUsers: {},
  activeBadge: { fontSize: 10, padding: '2px 7px', borderRadius: 10, background: 'rgba(16,185,129,0.15)', color: '#34d399', fontWeight: 700 },
  dangerMiniBtn: { background: 'rgba(244,63,94,0.1)', border: 'none', borderRadius: 6, padding: '2px 7px', cursor: 'pointer', fontSize: 10 },
  msgPanel: { flex: 1, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  msgPanelHdr: { padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  msgScroll: { flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 },
  msgRow: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '10px 14px', maxWidth: '75%', display: 'flex', flexDirection: 'column', gap: 4 },
  msgSender: { fontSize: 10, fontWeight: 700, color: '#818cf8', marginBottom: 2 },
  miniEdit: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, padding: '2px 4px' },
  miniDel: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, padding: '2px 4px' },
  thumb: { width: 80, height: 60, objectFit: 'cover', borderRadius: 6, marginTop: 4 },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, flex: 1 },
  empty: { color: '#475569', fontSize: 11, padding: 20, textAlign: 'center' },
  spinner: { width: 28, height: 28, border: '3px solid rgba(99,102,241,0.2)', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: '#111118', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 20, width: '100%', maxWidth: 640, boxShadow: '0 30px 80px rgba(0,0,0,0.6)' },
  modalHdr: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  closeBtn: { background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 18 },
  input: { padding: '9px 13px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#e2e8f0', fontSize: 12, outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  saveBtn: { padding: '10px 20px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 12 },
  dangerBtn: { flex: 1, padding: '10px', borderRadius: 10, border: '1px solid rgba(244,63,94,0.25)', background: 'rgba(244,63,94,0.12)', color: '#f43f5e', fontWeight: 700, cursor: 'pointer', fontSize: 12 },
  cancelBtn: { flex: 1, padding: '10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
};
